import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useRef,
} from 'react';

import Backdrop from '@material-ui/core/Backdrop';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AllInboxOutlinedIcon from '@material-ui/icons/AllInboxOutlined';
import CloseIcon from '@material-ui/icons/Close';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import EventNoteIcon from '@material-ui/icons/EventNote';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import GroupOutlinedIcon from '@material-ui/icons/GroupOutlined';
import LayersOutlinedIcon from '@material-ui/icons/LayersOutlined';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PublishOutlinedIcon from '@material-ui/icons/PublishOutlined';
import WatchLaterOutlinedIcon from '@material-ui/icons/WatchLaterOutlined';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../../Components';
import { getListAllAPI, getUserId, api } from '../../../services';
import { STATUS_ATIVO, formatDateDDMMYYYY } from '../../../utils';
import DialogoAdicionarProduto from '../DialogoAdicionarProduto';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'black',
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: 10000,
    color: '#fff',
  },
}));

const NOMES_ANDAR = ['Térreo', '1º Aéreo', '2º Aéreo'];

function getNomeAndar(andar) {
  return NOMES_ANDAR[andar - 1] || `Andar ${andar}`;
}

const DEFAULT_ID = -1;

const DialogoShow = forwardRef(({ handleClose }, ref) => {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [id, setId] = useState(DEFAULT_ID);
  const [dados, setDados] = useState({
    codigo: '',
    quantidade_andares: 1,
    status: STATUS_ATIVO,
  });
  const [itens, setItens] = useState([]);

  const [carregando, setCarregando] = useState(false);
  const [cont, setCont] = useState(0);

  const refDialogoAdicionarProduto = useRef(null);
  const refDialogoConfirmacaoSaida = useRef(null);

  const handleCloseDialog = () => {
    setOpen(false);
    if (handleClose) handleClose();
  };

  async function getDataResource(idLocal) {
    try {
      const data = await getListAllAPI(
        'enderecos_exposicao_mercadorias',
        ['id', 'asc'],
        { id: [idLocal] },
        [],
      );

      if (data.data.length > 0) {
        setId(data.data[0].id);
        setDados(data.data[0]);
        setCarregando(false);
        setCont((c) => c + 1);
      } else {
        throw 'Este endereço não existe!';
      }
    } catch (erros) {
      enqueueSnackbar(`${erros}`, {
        variant: 'error',
      });
      handleCloseDialog();
    }
  }

  useImperativeHandle(ref, () => ({
    handleOpen(idLocal) {
      setOpen(true);
      setCarregando(true);
      getDataResource(idLocal);
    },
  }));

  async function getItensTodosAndares() {
    setCarregando(true);
    const response = await getListAllAPI(
      'enderecos_exposicao_andares_produtos',
      ['andar', 'asc'],
      { endereco_exposicao_produto_id: id, status: STATUS_ATIVO },
      ['produto', 'validades'],
    );
    setItens(response.data);
    setCarregando(false);
  }

  useEffect(() => {
    if (id !== DEFAULT_ID) getItensTodosAndares();
  }, [cont, id]);

  function getItemDoAndar(andar) {
    return itens.find((item) => item.andar === andar) || null;
  }

  const andaresOcupados = itens.length;

  function handleAdicionarProduto(andar) {
    if (refDialogoAdicionarProduto.current) {
      refDialogoAdicionarProduto.current.handleOpen({
        endereco_exposicao_produto_id: id,
        andar,
      });
    }
  }

  function handleEditarProduto(row) {
    if (refDialogoAdicionarProduto.current) {
      refDialogoAdicionarProduto.current.handleOpen({
        endereco_exposicao_produto_id: id,
        andar: row.andar,
        andarProdutoExistente: row,
      });
    }
  }

  async function handleRemover(row) {
    try {
      await api.put(
        `/enderecos_exposicao_andares_produtos/status/inativar/${row.id}`,
        { user_id: getUserId() },
      );
      enqueueSnackbar('Produto removido do andar!', { variant: 'success' });
      setCont((c) => c + 1);
    } catch (erros) {
      enqueueSnackbar(`${erros}`, { variant: 'error' });
    }
  }

  function handleAbrirConfirmacaoSaida(row) {
    if (refDialogoConfirmacaoSaida.current) {
      refDialogoConfirmacaoSaida.current.handleOpen(
        'Registrar Saída',
        `Confirma a saída de "${
          row.produto ? row.produto.nome : ''
        }" do ${getNomeAndar(row.andar)}?`,
        row,
      );
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        aria-labelledby="form-dialog-title"
        disableBackdropClick
        disableEscapeKeyDown
        fullScreen
      >
        <DialogTitle
          id="form-dialog-title"
          onClose={handleCloseDialog}
        >{`Endereço de Exposição - ${dados.codigo}`}</DialogTitle>
        <DialogContent dividers>
          {carregando ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <Box p="1em">
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                style={{ gap: '10px' }}
                mb="1.5em"
              >
                <Chip
                  size="small"
                  icon={<LocationOnOutlinedIcon fontSize="small" />}
                  label={dados.status === STATUS_ATIVO ? 'Ativo' : 'Inativo'}
                  style={{
                    backgroundColor:
                      dados.status === STATUS_ATIVO ? '#e8f5e9' : '#fafafa',
                    color:
                      dados.status === STATUS_ATIVO ? '#2e7d32' : '#757575',
                    fontWeight: 'bold',
                  }}
                />
                <Chip
                  size="small"
                  icon={<LayersOutlinedIcon fontSize="small" />}
                  label={`${dados.quantidade_andares} andares`}
                />
                <Chip
                  size="small"
                  icon={<GroupOutlinedIcon fontSize="small" />}
                  label={`${andaresOcupados}/${dados.quantidade_andares} ocupados`}
                />
              </Box>

              {Array.from({ length: dados.quantidade_andares }).map(
                (_, index) => {
                  const andarAtual = index + 1;
                  const row = getItemDoAndar(andarAtual);
                  const ocupado = !!row;
                  const terreoExposicao =
                    andarAtual === 1 && !!dados.terreo_exposicao;
                  const enderecoInativo = dados.status !== STATUS_ATIVO;

                  return (
                    <Paper
                      key={andarAtual}
                      variant="outlined"
                      style={{
                        padding: '16px',
                        marginBottom: '16px',
                        borderRadius: '10px',
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: '10px' }}
                        >
                          <span
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '13px',
                              color: 'white',
                              backgroundColor: ocupado ? '#43a047' : '#bdbdbd',
                            }}
                          >
                            {andarAtual}
                          </span>
                          <Typography style={{ fontWeight: 'bold' }}>
                            {getNomeAndar(andarAtual)}
                          </Typography>
                          <Chip
                            size="small"
                            label={ocupado ? 'Ocupado' : 'Vazio'}
                            style={{
                              backgroundColor: ocupado ? '#e8f5e9' : '#f5f5f5',
                              color: ocupado ? '#2e7d32' : '#9e9e9e',
                              fontWeight: 'bold',
                            }}
                          />
                          {terreoExposicao ? (
                            <Chip
                              size="small"
                              label="Exposição em loja"
                              style={{
                                backgroundColor: '#ede7f6',
                                color: '#5e35b1',
                                fontWeight: 'bold',
                              }}
                            />
                          ) : null}
                        </Box>
                        {ocupado ? (
                          <Tooltip
                            title={
                              enderecoInativo
                                ? 'Endereço inativo — edição desabilitada'
                                : terreoExposicao
                                ? 'Térreo de exposição em loja — edição desabilitada'
                                : 'Editar'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={terreoExposicao || enderecoInativo}
                                onClick={() => handleEditarProduto(row)}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        mt="1em"
                        style={{ gap: '16px' }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                            border: '1px solid #e0e0e0',
                            flexShrink: 0,
                          }}
                        >
                          <AllInboxOutlinedIcon
                            style={{ color: ocupado ? '#43a047' : '#bdbdbd' }}
                          />
                        </Box>

                        {ocupado ? (
                          <Box flex="1">
                            <Typography style={{ fontWeight: 'bold' }}>
                              {row.produto ? row.produto.nome : ''}
                            </Typography>

                            {!terreoExposicao ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                                mt="0.5em"
                                style={{ gap: '6px' }}
                              >
                                <Typography
                                  variant="body2"
                                  style={{ color: '#757575' }}
                                >
                                  Validades:
                                </Typography>
                                {row.validades && row.validades.length > 0 ? (
                                  row.validades.map((validade) => (
                                    <Chip
                                      key={validade.id}
                                      size="small"
                                      icon={<EventNoteIcon fontSize="small" />}
                                      label={formatDateDDMMYYYY(
                                        validade.validade,
                                      )}
                                    />
                                  ))
                                ) : (
                                  <Typography variant="body2">-</Typography>
                                )}
                              </Box>
                            ) : null}

                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              flexWrap="wrap"
                              mt="0.5em"
                              style={{ gap: '8px' }}
                            >
                              {!terreoExposicao ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  flexWrap="wrap"
                                  style={{ gap: '8px' }}
                                >
                                  <Chip
                                    size="small"
                                    icon={
                                      <WatchLaterOutlinedIcon fontSize="small" />
                                    }
                                    label={
                                      row.dias_permanencia === null
                                        ? 'Permanência: -'
                                        : `Permanência: ${row.dias_permanencia} dia(s)`
                                    }
                                  />
                                  {row.data_entrada ? (
                                    <Chip
                                      size="small"
                                      icon={<EventNoteIcon fontSize="small" />}
                                      label={`desde ${formatDateDDMMYYYY(
                                        row.data_entrada,
                                      )}`}
                                    />
                                  ) : null}
                                </Box>
                              ) : (
                                <Box />
                              )}
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={terreoExposicao || enderecoInativo}
                                startIcon={<PublishOutlinedIcon />}
                                onClick={() => handleAbrirConfirmacaoSaida(row)}
                                style={
                                  terreoExposicao || enderecoInativo
                                    ? {}
                                    : {
                                        color: '#e53935',
                                        borderColor: '#e53935',
                                      }
                                }
                              >
                                Registrar saída
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            flex="1"
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            style={{ gap: '10px' }}
                          >
                            <Typography
                              variant="body2"
                              style={{ color: '#9e9e9e' }}
                            >
                              {enderecoInativo
                                ? 'Endereço inativo.'
                                : terreoExposicao
                                ? 'Térreo destinado à exposição em loja.'
                                : 'Este andar ainda não possui produto registrado.'}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={terreoExposicao || enderecoInativo}
                              startIcon={<GetAppOutlinedIcon />}
                              onClick={() => handleAdicionarProduto(andarAtual)}
                              style={
                                terreoExposicao || enderecoInativo
                                  ? {}
                                  : { color: '#43a047', borderColor: '#43a047' }
                              }
                            >
                              Registrar entrada
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  );
                },
              )}
            </Box>
          )}
        </DialogContent>
        <Backdrop
          className={classes.backdrop}
          open={carregando}
          onClick={() => {}}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>

      <DialogoAdicionarProduto
        ref={refDialogoAdicionarProduto}
        handleClose={() => setCont((c) => c + 1)}
      />
      <DialogoConfirmacao
        ref={refDialogoConfirmacaoSaida}
        handleConfirma={(row) => handleRemover(row)}
      />
    </>
  );
});

export default DialogoShow;
